import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Coins, Store, Ticket, BadgePercent } from "lucide-react";
import { api, errMsg, fdate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/lib/auth";
import { PageHeader, Hash, SimBadge, DemoBadge, Empty, CopyText, Loading } from "@/components/common";

const RULES = { reporte_validado: "Reporte validado", reporte_resuelto: "Reporte resuelto", pago_puntual: "Pago puntual", pago_multa: "Pago de multa", domiciliacion: "Domiciliación activada", tramite_emitido: "Trámite emitido", descuento_pago: "Descuento en pago", canje_comercio: "Canje en comercio", reembolso_tokens: "Reembolso" };

export default function Rewards() {
  const { refresh } = useAuth();
  const w = useFetch("/loyalty/wallet");
  const merchants = useFetch("/loyalty/merchants");
  const [confirm, setConfirm] = useState(null);

  const redeem = async () => {
    try {
      const { data } = await api.post("/loyalty/redeem", { merchant_id: confirm.m.merchant_id, reward_id: confirm.r.id });
      toast.success(`Cupón ${data.code} generado`);
      w.reload(); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setConfirm(null); }
  };

  return (
    <div>
      <PageHeader eyebrow="Lealtad ciudadana" title="Recompensas HGT" subtitle="Tokens por buen cumplimiento: canjéalos en comercios afiliados o como descuento en servicios municipales." actions={<SimBadge label="SMART CONTRACT SIMULADO" />} />
      {!w.data ? <Loading /> : (
        <div className="grid gap-4 lg:grid-cols-[1fr_1.6fr]">
          <div className="space-y-4">
            <Card className="bg-[#16202B] text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-white/70 text-sm"><Coins className="h-4 w-4" />Saldo disponible</div>
                <div className="text-5xl font-semibold font-display mt-2" data-testid="wallet-balance">{w.data.balance} <span className="text-xl text-white/70">HGT</span></div>
                <div className="text-sm text-white/70 mt-1">≈ ${(w.data.balance * w.data.token_value).toFixed(2)} MXN en descuentos municipales</div>
                <div className="mt-4 text-xs text-white/60">Wallet</div>
                <CopyText value={w.data.wallet} display={`${w.data.wallet.slice(0, 12)}…${w.data.wallet.slice(-6)}`} className="text-white" />
              </CardContent>
            </Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Cómo ganar tokens</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">{Object.entries(w.data.rules).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm"><span>{RULES[k] || k}</span><span className="font-mono text-emerald-700">+{v}</span></div>
              ))}</CardContent>
            </Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Ticket className="h-4 w-4" />Mis cupones</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {!w.data.coupons.length ? <div className="text-sm text-muted-foreground">Aún no canjeas recompensas.</div> : w.data.coupons.map((c) => (
                  <div key={c.coupon_id} className="rounded-lg border p-3" data-testid={`coupon-${c.code}`}>
                    <div className="text-sm font-medium">{c.reward}</div>
                    <div className="text-xs text-muted-foreground">{c.merchant} · {fdate(c.created_at)}</div>
                    <div className="flex justify-between items-center mt-1"><CopyText value={c.code} className="font-semibold" /><Hash value={c.tx.tx_hash} n={6} /></div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Store className="h-5 w-5" />Comercios afiliados</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {(merchants.data || []).map((m) => (
                <Card key={m.merchant_id} className="card-hover" data-testid={`merchant-${m.merchant_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2"><div><div className="font-medium">{m.name}</div><div className="text-xs text-muted-foreground">{m.giro} · {m.municipio}</div></div>{m.is_demo && <DemoBadge />}</div>
                    <div className="mt-2 text-xs rounded-md bg-[#FCE7C3]/60 px-2 py-1 inline-flex items-center gap-1"><BadgePercent className="h-3 w-3" />Beneficio municipal: {m.municipal_benefit}</div>
                    <div className="mt-3 space-y-2">
                      {m.rewards.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2">
                          <span className="text-sm">{r.title}</span>
                          <Button size="sm" variant="secondary" disabled={w.data.balance < r.cost} onClick={() => setConfirm({ m, r })} data-testid={`redeem-${r.id}`}>{r.cost} HGT</Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Movimientos on-chain</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {!w.data.history.length ? <Empty title="Sin movimientos" /> : w.data.history.slice(0, 15).map((h) => (
                  <div key={h.entry_id} className="flex items-center justify-between gap-2 text-sm border-b border-dashed pb-1.5">
                    <span>{RULES[h.rule] || h.rule} <span className="text-xs text-muted-foreground">{fdate(h.created_at)}</span></span>
                    <span className="flex items-center gap-2"><span className={`font-mono ${h.amount > 0 ? "text-emerald-700" : "text-rose-700"}`}>{h.amount > 0 ? "+" : ""}{h.amount}</span><Hash value={h.tx?.tx_hash} n={6} /></span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Canjear {confirm?.r.title}</AlertDialogTitle><AlertDialogDescription>Se transferirán {confirm?.r.cost} HGT al contrato de {confirm?.m.name}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={redeem} data-testid="redeem-confirm-button">Canjear</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
